import { useState, useEffect } from "react";
import styles from "../styles/eventos.module.css";
import { useNavigate, useLocation } from "react-router-dom";

import AñadirEvento from "./AñadirEvento";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import InfoModal from "../../../components/InfoModal";

import { useEventosList } from "../hooks/useEventosList";
import { useEventosSearchSort } from "../hooks/useEventosSearchSort";
import { EventosToolbar } from "../components/EventosToolbar";
import { EventoCard } from "../components/EventoCard";

function Eventos() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [eventoAEditar, setEventoAEditar] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [eventoAEliminar, setEventoAEliminar] = useState(null);

  // "pendientes" (por defecto) trae solo los que no han pasado; "pasados" el histórico.
  // Lo filtra el servidor (ver useEventosList) para no cargar siempre todo el histórico.
  const [estadoTiempo, setEstadoTiempo] = useState("pendientes");

  const {
    eventos,
    setEventos,
    loading,
    infoModal,
    setInfoModal,
    eliminarEvento,
    duplicarEvento,
  } = useEventosList(apiUrl, estadoTiempo);

  const {
    busqueda,
    setBusqueda,
    orden,
    setOrden,
    eventosFiltrados,
    eventosOrdenados,
  } = useEventosSearchSort(eventos);

  const location = useLocation();

  useEffect(() => {
    const ev = location.state?.editEvento;
    if (!ev) return;

    setEventoAEditar(ev);
    setMostrarFormulario(true);

    // Limpia el state para que no se reabra al volver atrás/refrescar
    navigate("/eventos", { replace: true, state: null });
  }, [location.state, navigate]);

  const verEvento = (id) => navigate(`/eventos/${id}`);

  // Si estábamos editando (llegamos aquí con estado de navegación desde la
  // ficha del evento), "Salir" vuelve atrás; si es un evento nuevo, solo cierra el formulario.
  const handleSalirFormulario = () => {
    if (eventoAEditar) {
      navigate(-1);
    } else {
      setMostrarFormulario(false);
      setEventoAEditar(null);
    }
  };

  const pedirConfirmacionEliminar = (evento) => {
    setEventoAEliminar(evento);
    setShowConfirm(true);
  };

  return (
    <div className={styles["eventos-container"]}>
      <div className={styles["perfiles-header-global"]}>
        <h1 className={styles["title"]}>Eventos</h1>

        {!mostrarFormulario ? (
          <button
            className={styles["add-button-global"]}
            onClick={() => setMostrarFormulario(true)}
          >
            + Añadir Evento
          </button>
        ) : (
          <button className={styles["add-button-global"]} onClick={handleSalirFormulario}>
            Salir
          </button>
        )}
      </div>

      {!mostrarFormulario && (
        <EventosToolbar
          busqueda={busqueda}
          setBusqueda={setBusqueda}
          orden={orden}
          setOrden={setOrden}
          resultados={eventosFiltrados.length}
          estadoTiempo={estadoTiempo}
          setEstadoTiempo={setEstadoTiempo}
        />
      )}

      {mostrarFormulario ? (
        <AñadirEvento
          evento={eventoAEditar}
          onClose={() => {
            setMostrarFormulario(false);
            setEventoAEditar(null);
          }}
          onEventoCreado={(nuevoEvento, opts = {}) => {
            setEventos((prev) => [...prev, nuevoEvento]);

            if (opts.close) {
              setMostrarFormulario(false);
              setEventoAEditar(null);
            }
          }}

          onEventoActualizado={(eventoActualizado, opts = {}) => {
            setEventos((prev) =>
              prev.map((ev) => (ev.id === eventoActualizado.id ? eventoActualizado : ev))
            );

            if (opts.close) {
              setMostrarFormulario(false);
              setEventoAEditar(null);
            }
          }}

        />
      ) : (
        <>
          {loading ? <div className={styles.muted}>Cargando eventos…</div> : null}

          {eventosOrdenados.map((evento) => (
            <EventoCard
              key={evento.id}
              evento={evento}
              onVer={verEvento}
              onEditar={(ev) => {
                setEventoAEditar(ev);
                setMostrarFormulario(true);
              }}
              onDuplicar={(ev) => duplicarEvento(ev.id)}
              onEliminar={pedirConfirmacionEliminar}
            />
          ))}
        </>
      )}

      {showConfirm && (
        <ConfirmDeleteModal
          title="Eliminar evento"
          message={
            <>
              ¿Seguro que deseas eliminar <strong>{eventoAEliminar?.nombre}</strong>?<br />
              Se eliminarán también actividades, cacerías, menús y participaciones asociadas.
            </>
          }
          onClose={() => {
            setShowConfirm(false);
            setEventoAEliminar(null);
          }}
          onConfirm={async () => {
            const id = eventoAEliminar?.id;
            if (!id) return;
            const { ok } = await eliminarEvento(id);
            if (ok) {
              setShowConfirm(false);
              setEventoAEliminar(null);
            }
          }}
        />
      )}

      {infoModal.open && (
        <InfoModal
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal({ open: false, title: "", message: "" })}
        />
      )}
    </div>
  );
}

export default Eventos;
